import { useFocusedField, getSortedFields, createFormFields, DefaultFieldLayout } from "@wix/form-fields";
import { deepEqual } from "fast-equals";
import { useMemo, useRef, useCallback, useImperativeHandle } from "react";
import { applyAllOverrides } from "../components/form/all-overrides.js";
import { clearFieldsWhenHiddenByRule } from "../components/form/clear-fields.js";
import { getStepIndex } from "../components/form/get-step-index.js";
import { fillEmptyValues } from "./form-values.js";
import { useFormView } from "./use-form-view.js";
import { useReportFieldEdit } from "./use-report-field-edit.js";
import { useScrollToFirstError } from "./use-scroll-to-first-error.js";
import { useValidation } from "./use-validation.js";
import { useFormValues } from "./use-form-values/use-form-values.js";
import { useSubmit } from "./use-submit/use-submit.js";
const useForm = ({
  form: paramForm,
  values: paramValues,
  stepId,
  onStepIdChange,
  onChange,
  onBlur,
  onFocus,
  errors,
  onValidate,
  onSubmit,
  onSubmitSuccess,
  onSubmitFailure,
  enableScrollToFirstError = true,
  forwardedRef,
  overrides,
  actions,
  config,
  breakpoint,
  validationMode,
  fieldMap,
  inProgress,
  enableAutofill
}) => {
  const formWithOverrides = useMemo(
    () => applyAllOverrides(paramForm, paramValues, overrides),
    [paramForm, paramValues, overrides]
  );
  const formId = formWithOverrides.id;
  const { setFocusedFieldId } = useFocusedField();
  const normalizedValues = useFormValues(
    formWithOverrides,
    paramValues,
    onChange,
    config,
    enableAutofill
  );
  const valuesRef = useRef(normalizedValues);
  valuesRef.current = normalizedValues;
  const errorsRef = useRef(errors);
  errorsRef.current = errors;
  const reportFieldEdit = useReportFieldEdit(formWithOverrides);
  const onFieldChange = (target, newValue) => {
    const oldValues = valuesRef.current;
    const oldErrors = errorsRef.current;
    if (!deepEqual(newValue, oldValues[target])) {
      reportFieldEdit({
        [target]: newValue
      });
      const newValues = {
        ...oldValues,
        [target]: newValue
      };
      const cleared = clearFieldsWhenHiddenByRule(
        paramForm,
        overrides,
        oldValues,
        newValues,
        oldErrors
      );
      onChange(cleared.values);
      onValidate(cleared.errors);
    }
  };
  const _onValidate = (newErrors) => {
    onValidate(newErrors);
  };
  const formViews = useFormView(formWithOverrides, stepId, breakpoint);
  const currentView = formViews[0];
  const { validateFields, validateStep, handleFieldValidation } = useValidation(
    {
      form: formWithOverrides,
      errorsRef,
      setErrors: _onValidate,
      validationMode
    }
  );
  const handleStepChange = useCallback(
    (step) => {
      const newStepId = formWithOverrides.steps?.[step]?.id;
      if (newStepId) {
        const firstFieldInStep = getSortedFields(formWithOverrides, {
          stepId: newStepId
        })[0];
        onStepIdChange?.(newStepId);
        setFocusedFieldId(firstFieldInStep?.id);
      }
    },
    [formWithOverrides, onStepIdChange, setFocusedFieldId]
  );
  const { scrollToFirstError, handleScrollToFirstError } = useScrollToFirstError({
    form: formWithOverrides,
    handleStepChange,
    currentStepId: stepId
  });
  const resetFormValues = useCallback(() => {
    onChange(fillEmptyValues(formWithOverrides, {}));
  }, [formWithOverrides, onChange]);
  const validateAndScrollToError = useCallback(
    async (values) => {
      const validationErrors = await validateFields(values);
      if (validationErrors && validationErrors.length) {
        handleScrollToFirstError(validationErrors);
      }
      return validationErrors;
    },
    [validateFields, handleScrollToFirstError]
  );
  const submitAction = useCallback(
    async (values) => {
      const response = await actions?.submitForm?.(formId, values);
      return response?.submission;
    },
    [actions, formId]
  );
  const { submitForm } = useSubmit({
    submitAction,
    onSubmit,
    onSubmitSuccess,
    onSubmitFailure,
    resetFormValues,
    validateAndScrollToError,
    values: normalizedValues,
    submitSettings: formWithOverrides.submitSettings,
    navigateToCheckout: actions?.navigateToCheckout,
    openPopup: actions?.openPopup,
    handleStepChange,
    onValidate: _onValidate,
    handleScrollToFirstError
  });
  useImperativeHandle(
    forwardedRef,
    () => ({
      submit: submitForm,
      validate: async () => {
        const validationErrors = await validateFields(normalizedValues);
        if (validationErrors && validationErrors.length > 0) {
          if (enableScrollToFirstError) {
            scrollToFirstError(validationErrors);
          }
          return false;
        }
        return true;
      },
      validateStep: (step) => validateStep(step, normalizedValues),
      getValues: () => normalizedValues
    }),
    [
      submitForm,
      validateStep,
      validateFields,
      normalizedValues,
      enableScrollToFirstError,
      scrollToFirstError
    ]
  );
  const currentStep = getStepIndex(stepId, formWithOverrides.steps);
  const setNextStep = useCallback(
    () => handleStepChange(currentStep + 1),
    [currentStep, handleStepChange]
  );
  const setPreviousStep = useCallback(
    () => handleStepChange(currentStep - 1),
    [currentStep, handleStepChange]
  );
  const fieldActions = useMemo(
    () => actions ? {
      actions: { ...actions, submitForm, setNextStep, setPreviousStep },
      formId,
      inProgress
    } : { actions: { submitForm, setNextStep, setPreviousStep } },
    [actions, formId, inProgress, submitForm, setNextStep, setPreviousStep]
  );
  const formFields = createFormFields({
    form: currentView,
    formSchema: formWithOverrides,
    values: normalizedValues,
    onFieldChange,
    onBlur,
    onFocus,
    errors,
    FieldLayout: DefaultFieldLayout,
    fields: fieldMap,
    validateFields: handleFieldValidation,
    fieldActions
  });
  return {
    currentView,
    formViews,
    normalizedValues,
    onFieldChange,
    errors,
    formFields,
    handleFieldValidation,
    formWithOverrides,
    currentStep,
    fieldActions,
    submitForm
  };
};
export {
  useForm
};
//# sourceMappingURL=use-form.js.map
